"use client"

import * as React from "react"
import { NavigationType, useLocation, useNavigate, useNavigationType } from "react-router-dom"

import {
  CREATE_PREFILL_STATE,
  createKindFromSearch,
  readCreatePrefill,
  withCreateParam,
  type CreateKind,
  type CreatePrefill,
} from "@/features/create-overlay/model/create-kind"
import { hasInAppHistory } from "@/shared/hooks/use-create-page-exit"

export type CreateOverlayApi = {
  /** The kind open over the current page, or null. */
  activeKind: CreateKind | null
  /** Open `kind` over the current page: pushes one history entry, so Back (or a phone swipe back) closes it. */
  open: (kind: CreateKind, prefill?: CreatePrefill) => void
  /** The current page with `kind` open, for a link (`<Link to={hrefFor(kind)}>`): following it pushes too. */
  hrefFor: (kind: CreateKind) => string
}

/**
 * The one door to the create overlay for entry points (menus, page buttons, tiles, the palette). Opening is just
 * a navigation to the current URL plus `?create=<kind>`; the host mounted in the app layout reads the param and
 * draws the form. Never navigate to a create route for these kinds.
 */
export function useCreateOverlay(): CreateOverlayApi {
  const navigate = useNavigate()
  const location = useLocation()
  const activeKind = createKindFromSearch(location.search)

  const hrefFor = React.useCallback(
    (kind: CreateKind) => `${location.pathname}${withCreateParam(location.search, kind)}${location.hash}`,
    [location.hash, location.pathname, location.search],
  )

  const open = React.useCallback(
    (kind: CreateKind, prefill?: CreatePrefill) => {
      if (activeKind === kind) return
      navigate(hrefFor(kind), {
        // Switching from one open kind to another replaces it: one overlay, one entry.
        replace: activeKind !== null,
        state: prefill ? { [CREATE_PREFILL_STATE]: prefill } : null,
      })
    },
    [activeKind, hrefFor, navigate],
  )

  return React.useMemo(() => ({ activeKind, open, hrefFor }), [activeKind, hrefFor, open])
}

export type CreateOverlayController = {
  activeKind: CreateKind | null
  prefill: CreatePrefill | null
  /** Cancel / ✕: step back when the overlay pushed its entry, else (opened from a link) drop the param in place. */
  close: () => void
  /** After a save: replace the overlay's entry with `href` (the created record), or just close it. */
  finish: (href?: string) => void
}

/**
 * History rules for the host. Whether the overlay's entry was pushed is decided when the param appears (a push
 * from inside the app) and kept while the same kind stays open, so a page that later rewrites its own search
 * params with a replace does not turn Cancel into a replace.
 */
export function useCreateOverlayController(): CreateOverlayController {
  const navigate = useNavigate()
  const location = useLocation()
  const navigationType = useNavigationType()
  const activeKind = createKindFromSearch(location.search)
  const prefill = React.useMemo(() => readCreatePrefill(location.state), [location.state])

  const openedRef = React.useRef<{ kind: CreateKind; pushed: boolean } | null>(null)
  if (activeKind === null) {
    openedRef.current = null
  } else if (openedRef.current?.kind !== activeKind) {
    openedRef.current = {
      kind: activeKind,
      pushed: navigationType === NavigationType.Push && hasInAppHistory(location),
    }
  }

  const close = React.useCallback(() => {
    const opened = openedRef.current
    if (!opened) return
    if (opened.pushed) {
      navigate(-1)
      return
    }
    navigate(
      { pathname: location.pathname, search: withCreateParam(location.search, null), hash: location.hash },
      { replace: true },
    )
  }, [location.hash, location.pathname, location.search, navigate])

  const finish = React.useCallback(
    (href?: string) => {
      if (href) {
        navigate(href, { replace: true })
        return
      }
      close()
    },
    [close, navigate],
  )

  return { activeKind, prefill, close, finish }
}
